import { AlertTriangle, X } from 'lucide-react';
import React from 'react';

import { cn } from '@/utils';

import {
  type CallQualityState,
  getMosBgColor,
  getMosColor,
  getMosQuality,
  type MosQuality,
} from '../hooks/useCallQuality';
import { Button } from './ui/button';

interface CallQualityPanelProps {
  callQuality: CallQualityState;
  edge: string | null;
  onClose: () => void;
}

const QUALITY_LABELS: Record<MosQuality, string> = {
  excellent: 'Excellent',
  good: 'Good',
  fair: 'Fair',
  poor: 'Poor',
  bad: 'Bad',
  unknown: 'N/A',
};

/** Inline sparkline rendered with SVG. */
function MosSparkline({ values }: { values: (number | null)[] }) {
  if (values.length < 2) {
    return (
      <div className="flex h-12 items-center justify-center text-[10px] text-muted-foreground">
        Collecting data...
      </div>
    );
  }

  const width = 280;
  const height = 48;
  const padding = 2;
  const drawWidth = width - padding * 2;
  const drawHeight = height - padding * 2;

  // MOS range: 1.0 to 4.5
  const minMos = 1.0;
  const maxMos = 4.5;

  const points = values
    .map((v, i) => {
      if (v === null) return null;
      const x = padding + (i / (values.length - 1)) * drawWidth;
      const y = padding + drawHeight - ((v - minMos) / (maxMos - minMos)) * drawHeight;
      return { x, y, v };
    })
    .filter(Boolean) as { x: number; y: number; v: number }[];

  if (points.length < 2) {
    return (
      <div className="flex h-12 items-center justify-center text-[10px] text-muted-foreground">
        Collecting data...
      </div>
    );
  }

  const pathD = points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`).join(' ');

  // Color the line based on the latest MOS value
  const latestMos = points[points.length - 1].v;
  const quality = getMosQuality(latestMos);
  const strokeColor = getSparklineColor(quality);

  // Quality zone backgrounds (horizontal bands)
  const yAt = (mos: number) =>
    padding + drawHeight - ((mos - minMos) / (maxMos - minMos)) * drawHeight;

  return (
    <svg
      width="100%"
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      preserveAspectRatio="none"
      className="rounded"
    >
      {/* Quality zone bands */}
      <rect
        x={0}
        y={yAt(4.5)}
        width={width}
        height={yAt(4.0) - yAt(4.5)}
        fill="rgb(34 197 94 / 0.08)"
      />
      <rect
        x={0}
        y={yAt(4.0)}
        width={width}
        height={yAt(3.5) - yAt(4.0)}
        fill="rgb(59 130 246 / 0.06)"
      />
      <rect
        x={0}
        y={yAt(3.5)}
        width={width}
        height={yAt(3.0) - yAt(3.5)}
        fill="rgb(234 179 8 / 0.06)"
      />
      <rect
        x={0}
        y={yAt(3.0)}
        width={width}
        height={yAt(1.0) - yAt(3.0)}
        fill="rgb(239 68 68 / 0.06)"
      />

      {/* Threshold lines */}
      <line
        x1={0}
        y1={yAt(3.5)}
        x2={width}
        y2={yAt(3.5)}
        stroke="currentColor"
        strokeWidth={0.5}
        opacity={0.15}
        strokeDasharray="4 4"
      />
      <line
        x1={0}
        y1={yAt(3.0)}
        x2={width}
        y2={yAt(3.0)}
        stroke="currentColor"
        strokeWidth={0.5}
        opacity={0.15}
        strokeDasharray="4 4"
      />

      {/* MOS line */}
      <path
        d={pathD}
        fill="none"
        stroke={strokeColor}
        strokeWidth={1.5}
        strokeLinejoin="round"
        strokeLinecap="round"
      />

      {/* Latest value dot */}
      <circle
        cx={points[points.length - 1].x}
        cy={points[points.length - 1].y}
        r={2.5}
        fill={strokeColor}
      />
    </svg>
  );
}

function getSparklineColor(quality: MosQuality): string {
  switch (quality) {
    case 'excellent':
      return 'rgb(34 197 94)';
    case 'good':
      return 'rgb(59 130 246)';
    case 'fair':
      return 'rgb(234 179 8)';
    case 'poor':
      return 'rgb(249 115 22)';
    case 'bad':
      return 'rgb(239 68 68)';
    default:
      return 'rgb(156 163 175)';
  }
}

type VolumeLevel = 'silent' | 'low' | 'normal' | 'loud';

function getVolumeLevel(raw: number): VolumeLevel {
  const pct = (raw / 32767) * 100;
  if (pct === 0) return 'silent';
  if (pct < 5) return 'low';
  if (pct < 50) return 'normal';
  return 'loud';
}

const VOLUME_CONFIG: Record<VolumeLevel, { label: string; color: string; textColor: string }> = {
  silent: { label: 'Silent', color: 'bg-destructive/60', textColor: 'text-destructive' },
  low: {
    label: 'Low',
    color: 'bg-attention-foreground/60',
    textColor: 'text-attention-foreground',
  },
  normal: {
    label: 'Good',
    color: 'bg-positive-foreground/60',
    textColor: 'text-positive-foreground',
  },
  loud: {
    label: 'Loud',
    color: 'bg-orange-500/60',
    textColor: 'text-orange-600 dark:text-orange-400',
  },
};

/** VU-meter style volume bar with label. */
function VolumeBar({ label, raw }: { label: string; raw: number }) {
  const pct = Math.min(Math.round((raw / 32767) * 100), 100);
  const level = getVolumeLevel(raw);
  const config = VOLUME_CONFIG[level];

  return (
    <div className="flex items-center gap-2 text-xs">
      <span className="w-14 shrink-0 text-muted-foreground">{label}</span>
      <div className="relative h-2 flex-1 rounded-full bg-muted overflow-hidden">
        <div
          className={cn(
            'absolute inset-y-0 left-0 rounded-full transition-all duration-300',
            config.color,
          )}
          style={{ width: `${pct}%` }}
        />
      </div>
      <span className={cn('w-10 text-right text-[10px] font-medium', config.textColor)}>
        {config.label}
      </span>
    </div>
  );
}

/** Single metric row in the debugger panel. */
function MetricRow({
  label,
  value,
  unit,
  warn,
}: {
  label: string;
  value: string;
  unit?: string;
  warn?: boolean;
}) {
  return (
    <div className="flex items-center justify-between text-xs">
      <span className="text-muted-foreground">{label}</span>
      <span className={cn('font-mono tabular-nums', warn && 'text-destructive font-semibold')}>
        {value}
        {unit && <span className="ml-0.5 text-muted-foreground">{unit}</span>}
      </span>
    </div>
  );
}

/**
 * Expandable call quality debugger panel.
 * Shows live network metrics, MOS sparkline, and active warnings.
 */
export function CallQualityPanel({ callQuality, edge, onClose }: CallQualityPanelProps) {
  const { current, mosHistory, activeWarnings } = callQuality;
  const quality = getMosQuality(current?.mos ?? null);
  const colorClass = getMosColor(quality);
  const bgClass = getMosBgColor(quality);

  return (
    <div className="border-t border-border bg-muted/30">
      {/* Panel header */}
      <div className="flex items-center justify-between px-4 py-2 border-b border-border">
        <span className="text-xs font-semibold text-foreground">Network Debugger</span>
        <Button variant="ghost" size="icon" className="h-5 w-5" onClick={onClose}>
          <X className="h-3 w-3" />
        </Button>
      </div>

      <div className="px-4 py-3 space-y-3">
        {/* MOS score hero */}
        <div className="flex items-center gap-3">
          <div className={cn('rounded-lg px-3 py-1.5 text-center', bgClass)}>
            <div className={cn('text-2xl font-bold tabular-nums', colorClass)}>
              {current?.mos !== null && current?.mos !== undefined ? current.mos.toFixed(1) : '--'}
            </div>
            <div className={cn('text-[10px] font-medium', colorClass)}>
              {QUALITY_LABELS[quality]}
            </div>
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] text-muted-foreground mb-1">MOS over time (60s)</div>
            <MosSparkline values={mosHistory} />
          </div>
        </div>

        {/* Metrics grid */}
        <div className="space-y-1.5">
          <MetricRow
            label="Round-trip time"
            value={current?.rtt !== undefined ? Math.round(current.rtt).toString() : '--'}
            unit="ms"
            warn={current ? current.rtt > 400 : false}
          />
          <MetricRow
            label="Jitter"
            value={current?.jitter !== undefined ? Math.round(current.jitter).toString() : '--'}
            unit="ms"
            warn={current ? current.jitter > 30 : false}
          />
          {/* Already a percentage, despite the name (see CallQualitySample). */}
          <MetricRow
            label="Packet loss"
            value={
              current?.packetsLostFraction !== undefined
                ? current.packetsLostFraction.toFixed(1)
                : '--'
            }
            unit="%"
            warn={current ? current.packetsLostFraction > 3 : false}
          />
          {/* Empty, not just absent: the codec is read off the wire, so it is
              unknown until the first codec stats entry arrives. */}
          <MetricRow label="Codec" value={current?.codecName || '--'} />
          <MetricRow label="Edge" value={edge ?? '--'} />
          <MetricRow
            label="Packets (sent/recv)"
            value={current ? `${current.packetsSent} / ${current.packetsReceived}` : '--'}
          />
        </div>

        {/* Audio levels */}
        {current && (
          <div className="space-y-1.5">
            <div className="text-[10px] font-medium text-muted-foreground">Audio Levels</div>
            <VolumeBar label="Mic" raw={current.audioInputLevel} />
            <VolumeBar label="Speaker" raw={current.audioOutputLevel} />
          </div>
        )}

        {/* Active warnings */}
        {activeWarnings.size > 0 && (
          <div className="space-y-1">
            <div className="text-[10px] font-medium text-muted-foreground">Active Warnings</div>
            {Array.from(activeWarnings).map(warning => (
              <div
                key={warning}
                className="flex items-center gap-1.5 rounded bg-attention-muted px-2 py-1 text-[11px] text-attention-foreground"
              >
                <AlertTriangle className="h-3 w-3 shrink-0" />
                {warning.replace(/-/g, ' ')}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
